/**
 * Copyright 2022 Phenix Real Time Solutions, Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as channelExpress from '../../../channelExpress';

function joinChannelAction() {
  return {
    type: 'JOIN_CHANNEL',
    success: 'Ready to view channel stream.',
    title: 'Joined Channel'
  };
}

function leaveChannelAction() {
  return {
    type: 'LEAVE_CHANNEL',
    success: 'Stopped viewing channel.',
    title: 'Left Channel'
  };
}

function leaveChannelFailureAction(error) {
  return {
    type: 'LEAVE_CHANNEL_FAILURE',
    success: `Unable to stop viewing channel. ${error.message}`,
    title: 'Unable to leave channel.'
  };
}

function joinChannelAlertAction(error) {
  return {
    type: 'JOIN_CHANNEL_ALERT',
    alert: `Unable to view channel. ${error.message}`,
    title: 'Failure Viewing Channel'
  };
}

function offlineAlertAction() {
  return {
    type: 'OFFLINE',
    alert: 'Unable to view channel.',
    title: 'Offline'
  };
}

function noChannelAction() {
  return {type: 'JOIN_NON_EXISTENT_CHANNEL'};
}

function accessDeniedAction() {
  return {type: 'CHANNEL_ACCESS_DENIED'};
}

function streamChangedAction(mediaStream) {
  return {
    type: 'STREAM_CHANGED',
    mediaStream
  };
}

function streamChangedSuccessAction(status) {
  return {
    type: 'STREAM_CHANGED_SUCCESS',
    success: `Stream Changed ${status}`
  };
}

function streamEndedAction(mediaStream) {
  return {
    type: 'STREAM_ENDED',
    mediaStream
  };
}

function streamEndedAlertAction() {
  return {
    type: 'STREAM_ENDED_ALERT',
    alert: 'Stream Ended'
  };
}

function streamChangedFailureAction(status) {
  return {
    type: 'STREAM_CHANGED_FAILURE',
    error: `Stream Changed ${status}`
  };
}

function unsupportedFeatureAction() {
  return {
    type: 'UNSUPPORTED_FEATURE',
    error: `Unsupported feature`
  };
}

function drmViewFailureAction(error) {
  return {
    type: 'DRM_ERROR',
    error
  };
}

function noTokenFailureAction(error) {
  return {
    type: 'NO_TOKEN_ERROR',
    error
  };
}

function joinChannel(delay, onStreamReady) {
  return function(dispatch) {
    return channelExpress.joinChannel(null, (error, response) => {
      if (onStreamReady) {
        if (typeof onStreamReady !== 'function') {
          throw new Error(`joinChannel [onStreamReady] must be a function. ${typeof onStreamReady} passed`);
        }

        if (delay !== null) {
          return setTimeout(() => {
            onStreamReady();
            streamChanged(dispatch, error, response);
          }, delay);
        }

        onStreamReady();
      }

      streamChanged(dispatch, error, response);
    }).then(() => {
      dispatch(joinChannelAction());
    }).catch((e) => {
      if (e.message.toLowerCase() === 'offline') {
        return dispatch(offlineAlertAction());
      }

      if (e.message.toLowerCase() === 'room-not-found') {
        if (onStreamReady) {
          onStreamReady();
        }

        return dispatch(noChannelAction());
      }

      if (e.message.toLowerCase() === 'unauthorized') {
        return dispatch(accessDeniedAction());
      }

      dispatch(joinChannelAlertAction(e));
    });
  };
}

function leaveChannel(callback) {
  return function(dispatch) {
    channelExpress.leaveChannel().then(() => {
      dispatch(leaveChannelAction());

      if (callback) {
        if (typeof callback !== 'function') {
          throw new Error(`leaveChannel [callback] must be a function. ${typeof callback} passed`);
        }

        callback();
      }
    }).catch(e => {
      dispatch(leaveChannelFailureAction(e));

      if (callback) {
        if (typeof callback !== 'function') {
          throw new Error(`leaveChannel [callback] must be a function. ${typeof callback} passed`);
        }

        callback();
      }
    });
  };
}

function streamChanged(dispatch, error, response) {
  if (error && error.message.toLowerCase() === 'gone') {
    return dispatch(streamChangedAction(null));
  }

  if (error) {
    return dispatch(joinChannelAlertAction(error));
  }

  if (response.status === 'device-insecure') {
    return dispatch(drmViewFailureAction(new Error(response.status)));
  }

  if (response.status === 'ended' || response.status === 'stream-ended' || response.status === 'origin-ended' || response.status === 'origin-stream-ended') {
    dispatch(streamEndedAction(response.mediaStream));

    return dispatch(streamEndedAlertAction());
  }

  if (response.status === 'no-stream-playing') {
    return dispatch(streamChangedAction(null));
  }

  if (response.status === 'unsupported-features') {
    dispatch(streamChangedAction(null));

    return dispatch(unsupportedFeatureAction(null));
  }

  if (response.status === 'unauthorized') {
    dispatch(streamChangedAction(null));

    return dispatch(accessDeniedAction());
  }

  if (response.status !== 'ok') {
    dispatch(streamChangedAction(response.mediaStream));

    return dispatch(streamChangedFailureAction(response.status));
  }

  dispatch(streamChangedAction(response.mediaStream));
  dispatch(streamChangedSuccessAction(response.reason));
}

export {joinChannel, leaveChannel, noTokenFailureAction};